import React, { useEffect } from 'react';

import { useDisclosure } from '@mantine/hooks';

const DEFAULT_ANIMATION_DURATION = 200;

/**
 * A component that animates the visibility of its children with a fade effect.
 *
 * @component
 * @param {Object} props - The component props
 * @param {number} props.animationDuration - The duration of the animation in milliseconds
 * @param {React.ReactNode} props.children - The content to be animated
 * @param {boolean} props.visible - Controls whether the content should be visible
 * @param {boolean} [props.initialVisibility=false] - Sets whether content is visible on first render
 * @param {(isVisible: boolean) => void} [props.onVisibilityChange] - Callback fired when visibility changes
 *
 * @example
 * ```jsx
 * <AnimatedDiv
 *   visible={isOpen}
 *   initialVisibility={true}
 *   onVisibilityChange={(visible) => console.log('Visibility changed:', visible)}
 * >
 *   <div className='animate-some-defined-tailwindconfig-animation'>Animated content</div>
 * </AnimatedDiv>
 * ```
 *
 * @remarks
 * The component uses a timer to delay hiding content to allow for animations to complete.
 * The actual animation duration is controlled by the DEFAULT_ANIMATION_DURATION constant.
 * See NeoMainMenu.tsx for an example of how to use this component.
 */
export const AnimatedDiv = ({
  animationDuration,
  children,
  initialVisibility,
  onVisibilityChange,
  visible,
}: {
  animationDuration?: number;
  children: React.ReactNode;
  initialVisibility?: boolean;
  onVisibilityChange?: (val: boolean) => void;
  visible: boolean;
}) => {
  const [hidden, { open: hide, close: show }] = useDisclosure(
    !initialVisibility,
  );

  useEffect(() => {
    if (visible) {
      show();
      onVisibilityChange?.(true);
      return () => {};
    }
    const closeTimer = setTimeout(
      () => {
        onVisibilityChange?.(false);
        hide();
      },
      (animationDuration ?? DEFAULT_ANIMATION_DURATION) * 0.9,
    );
    return () => {
      clearTimeout(closeTimer);
    };
  }, [animationDuration, hide, onVisibilityChange, show, visible]);

  if (hidden) {
    return null;
  }

  return <>{children}</>;
};
